import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Pencil, Wallet } from "lucide-react";
import { showErrorToast } from "../../application/utils/error-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@tabipla/ui/components/dialog";
import { Button, Input, Badge } from "@tabipla/ui";
import { useUpdateTrip } from "../../application/hooks/useTrips";
import { PlaceAutocomplete, type PlaceResult } from "./PlaceAutocomplete";
import { TripDatePicker } from "./TripDatePicker";
import type { TripResponseDto } from "@tabipla/shared";

const INTERESTS = [
  { value: "sightseeing", label: "観光", emoji: "🏛️" },
  { value: "food", label: "グルメ", emoji: "🍜" },
  { value: "nature", label: "自然", emoji: "🌲" },
  { value: "culture", label: "文化", emoji: "🎭" },
  { value: "shopping", label: "ショッピング", emoji: "🛍️" },
  { value: "adventure", label: "アドベンチャー", emoji: "🎢" },
  { value: "relaxation", label: "リラクゼーション", emoji: "♨️" },
];

interface EditTripDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trip: TripResponseDto;
}

export function EditTripDialog({ open, onOpenChange, trip }: EditTripDialogProps) {
  const { t } = useTranslation();
  const updateMutation = useUpdateTrip(trip.id);

  const [formData, setFormData] = useState({
    title: trip.title,
    destination: trip.destination,
    startDate: trip.startDate?.split("T")[0] ?? "",
    endDate: trip.endDate?.split("T")[0] ?? "",
    budget: trip.budget?.toString() ?? "",
    interests: trip.interests ?? [],
  });

  // Sync form data when trip changes
  useEffect(() => {
    setFormData({
      title: trip.title,
      destination: trip.destination,
      startDate: trip.startDate?.split("T")[0] ?? "",
      endDate: trip.endDate?.split("T")[0] ?? "",
      budget: trip.budget?.toString() ?? "",
      interests: trip.interests ?? [],
    });
  }, [trip]);

  const submitUpdate = async () => {
    // Build payload with only truthy values to avoid validation issues
    const payload: {
      title: string;
      destination: string;
      startDate?: string;
      endDate?: string;
      budget?: number;
      interests?: string[];
    } = {
      title: formData.title,
      destination: formData.destination,
    };

    if (formData.startDate) {
      payload.startDate = formData.startDate;
    }
    if (formData.endDate) {
      payload.endDate = formData.endDate;
    }
    if (formData.budget) {
      const budgetValue = parseInt(formData.budget, 10);
      if (budgetValue > 0) {
        payload.budget = budgetValue;
      }
    }
    if (formData.interests.length > 0) {
      payload.interests = formData.interests;
    }

    await updateMutation.mutateAsync(payload);
    toast.success(t("trips.updateSuccess", "旅行を更新しました"));
    onOpenChange(false);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await submitUpdate();
    } catch (error) {
      showErrorToast(error, t, { onRetry: submitUpdate });
    }
  };

  const toggleInterest = (interest: string) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest],
    }));
  };

  const handleDestinationChange = useCallback((place: PlaceResult | null) => {
    if (place) {
      setFormData((prev) => ({
        ...prev,
        destination: place.name || place.formattedAddress,
      }));
    } else {
      setFormData((prev) => ({ ...prev, destination: "" }));
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="flex max-w-lg flex-col gap-0 overflow-hidden p-0 sm:p-0"
      >
        <DialogHeader className="shrink-0 border-b bg-background px-6 pb-4 pt-2 sm:pt-6">
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" />
            {t("trips.editTitle", "旅行を編集")}
          </DialogTitle>
          <DialogDescription>
            {t("trips.editDescription", "旅行の基本情報を変更できます")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSave} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-4">
            {/* Title */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("trips.tripTitle")}</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t("trips.titlePlaceholder")}
                required
              />
            </div>

            {/* Destination */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("trips.destination")}</label>
              <PlaceAutocomplete
                value={formData.destination}
                onChange={handleDestinationChange}
                placeholder={t("trips.destinationPlaceholder")}
              />
            </div>

            {/* Dates */}
            <TripDatePicker
              startDate={formData.startDate}
              endDate={formData.endDate}
              onStartDateChange={(date) => setFormData({ ...formData, startDate: date })}
              onEndDateChange={(date) => setFormData({ ...formData, endDate: date })}
            />

            {/* Budget */}
            <div className="space-y-2">
              <label className="flex items-center gap-1.5 text-sm font-medium">
                <Wallet className="h-4 w-4 text-muted-foreground" />
                {t("trips.budget")}
                <span className="text-xs text-muted-foreground">
                  ({t("common.optional", "任意")})
                </span>
              </label>
              <Input
                type="number"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                placeholder={t("trips.budgetPlaceholder")}
              />
            </div>

            {/* Interests */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("trips.interests")}</label>
              <div className="flex flex-wrap gap-2">
                {INTERESTS.map((interest) => (
                  <Badge
                    key={interest.value}
                    variant={formData.interests.includes(interest.value) ? "default" : "outline"}
                    className="cursor-pointer transition-all hover:scale-105 active:scale-95"
                    onClick={() => toggleInterest(interest.value)}
                  >
                    <span className="mr-1">{interest.emoji}</span>
                    {interest.label}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Actions - Sticky footer */}
          <div className="sticky bottom-0 flex gap-3 border-t bg-background px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={updateMutation.isPending} className="flex-1">
              {updateMutation.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
